import fs from 'fs';
import path from 'path';

/**
 * User Prompt Error Logger - Captures detailed errors for USER_PROMPT_REQ
 * Logs full error details including raw strings for parse failures
 */

class UserPromptErrorLogger {
    private logStream: fs.WriteStream | null = null;
    private logPath: string;
    private enabled: boolean;
    private hasErrors: boolean = false;

    constructor() {
        // Get log path from environment or use default
        this.logPath = process.env.USER_PROMPT_ERROR_LOG_PATH || path.join(process.cwd(), 'user-prompt-req-errors');
        this.enabled = process.env.USER_PROMPT_ERROR_LOGGING !== 'false';
    }

    /**
     * Reset the error log file for a new request
     */
    resetLogFile(requestContext?: string): void {
        if (!this.enabled) return;

        try {
            // Close existing stream
            if (this.logStream) {
                this.logStream.end();
                this.logStream = null;
            }

            // Ensure directory exists
            const dir = path.dirname(this.logPath);
            if (dir !== '.' && !fs.existsSync(dir)) {
                fs.mkdirSync(dir, { recursive: true });
            }

            // Truncate the file by opening in write mode (not append)
            this.logStream = fs.createWriteStream(this.logPath, { flags: 'w' });
            this.hasErrors = false;

            // Write header
            const header = `================================================================================
USER PROMPT REQUEST ERROR LOG
Request Started: ${new Date().toISOString()}
${requestContext ? `Context: ${requestContext}` : ''}
================================================================================

`;
            this.logStream.write(header);

        } catch (error) {
            console.error('[UserPromptErrorLogger] Failed to reset log file:', error);
        }
    }

    /**
     * Log a JSON parse error with the raw string that failed
     */
    logJsonParseError(context: string, rawString: string, error: Error): void {
        if (!this.enabled) return;
        this.hasErrors = true;

        const entry = `
--------------------------------------------------------------------------------
[${new Date().toISOString()}] JSON PARSE ERROR
--------------------------------------------------------------------------------
Context: ${context}
Error: ${error.message}

Raw String (${rawString.length} chars):
--------------------------------------------------------------------------------
${rawString}
--------------------------------------------------------------------------------

Stack Trace:
${error.stack || 'No stack trace available'}

`;
        this.write(entry);
        console.error(`[UserPromptError] JSON Parse Error in ${context}: ${error.message}`);
    }

    /**
     * Log a general error with full details
     */
    logError(context: string, error: Error | string, additionalData?: Record<string, any>): void {
        if (!this.enabled) return;
        this.hasErrors = true;

        const errorMessage = error instanceof Error ? error.message : error;
        const errorStack = error instanceof Error ? error.stack : undefined;

        let entry = `
--------------------------------------------------------------------------------
[${new Date().toISOString()}] ERROR
--------------------------------------------------------------------------------
Context: ${context}
Error: ${errorMessage}
`;

        if (additionalData) {
            entry += `
Additional Data:
${JSON.stringify(additionalData, null, 2)}
`;
        }

        if (errorStack) {
            entry += `
Stack Trace:
${errorStack}
`;
        }

        entry += `--------------------------------------------------------------------------------

`;
        this.write(entry);
        console.error(`[UserPromptError] ${context}: ${errorMessage}`);
    }

    /**
     * Log a SQL query error with the full query
     */
    logSqlError(query: string, error: Error | string, params?: any[]): void {
        if (!this.enabled) return;
        this.hasErrors = true;

        const errorMessage = error instanceof Error ? error.message : error;

        const entry = `
--------------------------------------------------------------------------------
[${new Date().toISOString()}] SQL QUERY ERROR
--------------------------------------------------------------------------------
Error: ${errorMessage}

Query (${query.length} chars):
--------------------------------------------------------------------------------
${query}
--------------------------------------------------------------------------------
${params ? `\nParameters: ${JSON.stringify(params)}` : ''}

`;
        this.write(entry);
        console.error(`[UserPromptError] SQL Error: ${errorMessage}`);
    }

    /**
     * Log an LLM API error
     */
    logLlmError(provider: string, model: string, method: string, error: Error | string, requestData?: any): void {
        if (!this.enabled) return;
        this.hasErrors = true;

        const errorMessage = error instanceof Error ? error.message : error;
        const errorStack = error instanceof Error ? error.stack : undefined;

        let entry = `
--------------------------------------------------------------------------------
[${new Date().toISOString()}] LLM API ERROR
--------------------------------------------------------------------------------
Provider: ${provider}
Model: ${model}
Method: ${method}
Error: ${errorMessage}
`;

        if (requestData) {
            // Truncate large request data
            const dataStr = JSON.stringify(requestData, null, 2);
            const truncated = dataStr.length > 5000 ? dataStr.substring(0, 5000) + '\n... [truncated]' : dataStr;
            entry += `
Request Data:
${truncated}
`;
        }

        if (errorStack) {
            entry += `
Stack Trace:
${errorStack}
`;
        }

        entry += `--------------------------------------------------------------------------------

`;
        this.write(entry);
        console.error(`[UserPromptError] LLM Error (${provider}/${model}): ${errorMessage}`);
    }

    /**
     * Log tool execution error
     */
    logToolError(toolName: string, toolInput: any, error: Error | string): void {
        if (!this.enabled) return;
        this.hasErrors = true;

        const errorMessage = error instanceof Error ? error.message : error;
        const errorStack = error instanceof Error ? error.stack : undefined;

        const entry = `
--------------------------------------------------------------------------------
[${new Date().toISOString()}] TOOL EXECUTION ERROR
--------------------------------------------------------------------------------
Tool: ${toolName}
Error: ${errorMessage}

Tool Input:
${JSON.stringify(toolInput, null, 2)}
${errorStack ? `\nStack Trace:\n${errorStack}` : ''}
--------------------------------------------------------------------------------

`;
        this.write(entry);
        console.error(`[UserPromptError] Tool Error (${toolName}): ${errorMessage}`);
    }

    /**
     * Write final summary if there were errors
     */
    writeSummary(): void {
        if (!this.enabled || !this.hasErrors) return;

        const summary = `
================================================================================
REQUEST COMPLETED WITH ERRORS
Time: ${new Date().toISOString()}
================================================================================
`;
        this.write(summary);
    }

    /**
     * Check if any errors were logged
     */
    hadErrors(): boolean {
        return this.hasErrors;
    }

    private write(content: string): void {
        if (this.logStream) {
            this.logStream.write(content);
        }
    }
}

// Export singleton instance
export const userPromptErrorLogger = new UserPromptErrorLogger();
